import React, { useState, useCallback } from "react";
import styled, { css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFlag,
  faClock,
  faFileArchive,
} from "@fortawesome/free-regular-svg-icons";
import { faCaretRight, faShareAlt } from "@fortawesome/free-solid-svg-icons";
import { hoverTips } from "utils/mixin";
import data from "services/data";
import date from "utils/date";
import logger from "utils/logger";
import ToolGroup from "./toolGroup";
import PluginDepModal from "./modals/pluginDepModal";
import ConfirmModal from "./modals/confirmModal";
import { Link } from "react-router-dom";
import notifier from "utils/notifier";

const StyledIcon = styled(FontAwesomeIcon)``;

const StyledDepList = styled.div`
  position: relative;
  border-top: 1px solid ${({ theme }) => theme.borderGray};
  padding: 16px 0 24px 0;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const StyledHeader = styled.div`
  color: ${({ theme }) => theme.black};
  width: fit-content;
  user-select: none;
  cursor: pointer;
  ${StyledIcon} {
    transition: 0.3s;
  }
  transition: 0.4s;

  ${({ active }) =>
    active
      ? css`
          ${StyledIcon} {
            transform: rotate(90deg);
          }
        `
      : css`
          ${StyledIcon} {
            transform: rotate(0);
          }
        `}
`;

const StyledContentClip = styled.div`
  overflow: hidden;
  position: relative;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  /* border-bottom: 1px solid ${({ theme }) => theme.borderGray}; */
  border-radius: 6px;
  overflow: hidden;
  position: absolute;
  transition: 0.3s ease-in-out;
  ${({ active }) =>
    active
      ? css`
          position: relative;
          transform: translateY(0);
        `
      : css`
          transform: translateY(-100%);
        `}
`;

const StyledRow = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.borderGray};
  :last-child {
    border-bottom: none;
  }
  padding: 8px;
  color: ${({ theme }) => theme.black};
  display: flex;
  justify-content: space-between;
`;

const StyledName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lsmall};
  display: flex;
`;

const StyledA = styled.a`
  color: unset;
  text-decoration: none;
  position: relative;

  ::after {
    content: "";
    width: 100%;
    height: 1px;
    position: absolute;
    left: 0;
    bottom: 0.1rem;
    background-color: ${({ theme }) => theme.fontColor};
    transition: 0.2s;
    transform: scaleX(0);
  }

  :hover {
    ::after {
      transform: scaleX(1);
    }
  }
`;

const StyledInfo = styled.div`
  display: flex;
  column-gap: 7px;
  align-items: baseline;
`;

const StyledId = styled.div`
  font-family: ${({ theme }) => theme.font.youshe};
  color: ${({ theme }) => theme.infoBlue};
  margin-right: 4px;
  margin-left: 8px;
`;

const StyledVersion = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lsmall};
  color: ${({ theme }) => theme.infoBlue};
`;

const StyledBuildTime = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lsmall};
`;

const StyledToolGroup = styled(ToolGroup)`
  position: absolute;
  right: 8px;
  top: 16px;
`;

const StyledRowToolGroup = styled(ToolGroup)``;

const DepList = ({ auth, pluginVerData, onDepChange }) => {
  const [open, setOpen] = useState(true);
  const [modalVisible, setModalVisible] = useState({
    pluginDepModal: false,
    confirmModal: false,
  });
  const [modalType, setModalType] = useState("");
  const [currentDep, setCurrentDep] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const headerTools = [
    {
      type: "add",
      size: "1x",
      title: "添加依赖",
      onClick: () => {
        setModalType("add");
        setModalVisible({ pluginDepModal: true });
        logger.log("点击添加插件版本", pluginVerData);
      },
    },
  ];
  const rowTools = [
    {
      type: "remove",
      size: "1x",
      title: "删除依赖",
      titlePosition: { left: "-70px", top: "0" },
      onClick: (depVer) => {
        setModalVisible({ confirmModal: true });
        logger.log("点击删除插件版本依赖", depVer);
        setCurrentDep(depVer);
      },
    },
  ];
  const closeAllModal = useCallback(() => {
    setModalVisible({});
    // 清除错误信息
    setErrorMessage("");
  }, []);

  const handlePluginVerDepAdd = useCallback(
    async (form, pluginVerData) => {
      try {
        logger.log("发送请求 添加插件依赖", pluginVerData.id, form);
        const { depPlugVerId } = form;
        await data.addPluginVerDep(pluginVerData.id, depPlugVerId);
        // 关闭modal
        closeAllModal();
        // 刷新当前页
        onDepChange && onDepChange();
      } catch (error) {
        // setErrorMessage(error.message);
        notifier.error(error.message)
      }
    },
    [closeAllModal, onDepChange]
  );

  const handlePluginVerDepDelete = useCallback(async () => {
    try {
      logger.log("发送请求 删除插件依赖", currentDep);
      await data.deletePluginVerDep(pluginVerData.id, currentDep.id);
      // 关闭modal
      closeAllModal();
      // 刷新当前页
      onDepChange && onDepChange();
    } catch (error) {
      // setErrorMessage(error.message);
      notifier.error(error.message)
    }
  }, [closeAllModal, currentDep, onDepChange, pluginVerData]);

  const list = pluginVerData.depPluginVerList;

  return (
    <StyledDepList>
      <StyledToolGroup auth={auth} tools={headerTools} />
      <StyledHeader
        active={open}
        onClick={() => setOpen(!open)}
        title={open ? "收起" : "展开"}
      >
        <StyledIcon icon={faShareAlt} size="sm" /> 版本依赖
      </StyledHeader>
      <StyledContentClip>
        <StyledContent active={open}>
          {list.map((depVer) => (
            <StyledRow key={depVer.id}>
              <StyledName>
                <StyledIcon icon={faFileArchive} />
                <StyledId>#{depVer.id}</StyledId>
                <StyledA
                  title={`点击查看该插件详情：${depVer.pluginName} #${depVer.id}`}
                  href={`#/pluginVer/releases?pluginName=${encodeURIComponent(
                    depVer.pluginName
                  )}`}
                  // target="_blank"
                  // rel="noopener noreferer"
                >
                  {depVer.pluginName}
                </StyledA>
              </StyledName>
              <StyledInfo>
                <StyledVersion title="version 版本号">
                  {depVer.version}
                </StyledVersion>
                {/* /
                <StyledBuildTime title="build time 编译时间">
                  {depVer.buildTime}
                </StyledBuildTime> */}
                <StyledRowToolGroup
                  auth={auth}
                  tools={rowTools}
                  data={depVer}
                />
              </StyledInfo>
            </StyledRow>
          ))}
        </StyledContent>
      </StyledContentClip>
      <PluginDepModal
        errorMessage={errorMessage}
        visible={modalVisible.pluginDepModal}
        type={modalType}
        pluginVerData={pluginVerData}
        onClose={closeAllModal}
        onAddDep={handlePluginVerDepAdd}
      />
      <ConfirmModal
        errorMessage={errorMessage}
        visible={modalVisible.confirmModal}
        onClose={closeAllModal}
        title="提示"
        content="确定要删除此插件版本依赖？"
        confirmLabel="删除"
        cancelLabel="取消"
        confirmType="danger"
        onConfirm={handlePluginVerDepDelete}
        onCancel={closeAllModal}
      />
    </StyledDepList>
  );
};

export default DepList;
